import React, { useEffect, useRef, useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery } from '@apollo/client';
import { listQuery } from './queries';
import { mapToListingData, toLookups } from './utils';
import { filterSectionsContent, headers } from './content';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IAbstractRecord } from '@models';
import _ from 'lodash';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useAppSelector } from '@redux/hooks';
import SalesForceBusinessUserDrawer from '../salesforce-business-users-drawer/SalesForceBusinessUserDrawer';
import { capitalizeFirstCharacter } from '@utils/formatting-utils';
import DateService from '@services/DateService';
import { IEnhancedMenuItem } from '@components/table/types';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IFilterModel } from '@components/widgets/listing-widget';

const PAGE_CONTEXT = 'BusinessUsers';
const FILTER_SESSION_KEY = 'businessUsersFilter';

const BusinessUsersPage: React.FC = () => {
  const user = useAppSelector((state) => state.user);
  const isAdmin = user.userRoles.includes('Insurance-Admin');

  const [loading, setLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();

  const filterLovs = useRef<{
    type: Record<string, string>;
    access: Record<string, string>;
    relatedCompany: Record<string, string>;
    status: Record<string, string>;
  }>();

  const [drawerOpen, setDrawerOpen] = useState<{ open: boolean; id?: string }>({
    open: false,
  });

  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          selectedTypes: [],
          selectedAccesses: [],
          selectedRelatedCompany: [],
          businessUserStatus: [],
          createdOn: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });

  const [getBusinessUsersLazy] = useLazyQuery(listQuery());

  const actions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'new',
          title: '+ New',
          onClick: () => {
            setDrawerOpen({ open: true });
          },
        },
      ]
    : [];

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    getListingData(filterValues);
  };

  const getListingData = (
    filter = filterValues,
    newFilterLovs?: IAbstractRecord
  ) => {
    if (!filter) {
      setLoading(false);
      return {};
    }

    setFilter(filter, FILTER_SESSION_KEY);
    setFilterValues(filter);

    const filterV = filter.namedFilters;
    const pagination = filter.pagination;

    setLoading(true);

    const formattedDateRange = DateService.formatDateRangeBackend(
      filterV.createdOn
    );

    return getBusinessUsersLazy({
      variables: {
        currentPage: pagination.pageNumber,
        currentPageSize: pagination.pageSize,
        selectedTypes: filterV.type && filterV.type.length ? filterV.type : [],
        selectedAccesses:
          filterV.access && filterV.access.length ? filterV.access : [],
        businessUserStatus:
          filterV.status && filterV.status.length ? filterV.status : [],
        createdDateFrom: formattedDateRange?.[0],
        createdDateTo: formattedDateRange?.[1],
        keywordSearch: filter.searchKey || null,
        Attribute:
          capitalizeFirstCharacter(filter.orderBy) ||
          'salesforceManagement_BusinessUser_Code',
        Descending: filter.descending,
      },
    })
      .then(({ data }) => {
        if (data) {
          const newTableData = mapToListingData(data);
          setTableData(newTableData);
          const newLovs = toLookups(data);
          filterLovs.current = {
            ...filterLovs.current,
            ...newLovs,
          };
          const newFilterSections = filterSectionsContent(
            filterLovs.current,
            filterV
          );
          setFilterSections(newFilterSections);
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePageChange = async (
    page: number,
    filterModel = filterValues
  ): Promise<void> => {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  };

  const onFilterUpdate = (namedFilters: IAbstractRecord) => {
    const updatedFilterModel: IFilterModel = {
      ...filterValues,
      namedFilters: { ...filterValues.namedFilters, ...namedFilters },
      pagination: {
        ...filterValues.pagination,
        pageNumber: 1,
      },
    };
    getListingData(updatedFilterModel);
  };

  const renderMainChildren = () => (
    <>
      <ListingWidget
        title="Business Users"
        name="businessUsers"
        orderByAscendingByDefault
        data={tableData}
        tableSettings={{
          headers,
        }}
        loading={loading}
        defaultOrderByColumn="salesforceManagement_BusinessUser_Code"
        pageContext={PAGE_CONTEXT}
        onPageChange={(filterModel) => {
          const newFilterModel = {
            ...filterValues,
            ...filterModel,
          };
          const page = filterModel.pagination.pageNumber;
          return handlePageChange(page, newFilterModel);
        }}
        actions={actions}
        disableSelection
        usePagination
      />
      {drawerOpen.open && (
        <SalesForceBusinessUserDrawer
          open={drawerOpen.open}
          onClose={() => setDrawerOpen({ open: false })}
          onSuccess={() => {
            handlePageChange(1);
          }}
          personId={drawerOpen.id}
          salesForceBusinessUserDetailsInfo={undefined}
        />
      )}
    </>
  );

  const renderFilter = () =>
    filterSections && (
      <ListingFilterWidget
        name=""
        disabled={loading}
        filters={filterSections}
        onApplyFilter={onFilterUpdate}
      />
    );

  return (
    <StaticLayout
      name="BusinessUsers"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default BusinessUsersPage;
